// src/components/gamification/SocialProfileAnalytics.tsx
// Displays all social profiles with their analytics, regardless of gamification status
// Shows profile cards with social analytics from the configured provider.

import React from 'react';
import { useQuery, useAction } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Id, Doc } from '@/convex/_generated/dataModel';
import { GamifiedProfileCard } from './GamifiedProfileCard';
import { motion } from 'framer-motion';
import { Share2, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface SocialProfileAnalyticsProps {
	userId: Id<'users'>;
	className?: string;
}

const toMetricNumber = (value: unknown): number => {
	const numeric = typeof value === 'number' ? value : Number(value);
	return Number.isFinite(numeric) ? numeric : 0;
};

const canonicalSocialPlatform = (platform: unknown): string => {
	const normalized = String(platform || '')
		.toLowerCase()
		.replace(/[\s_-]+/g, '');
	return ['x', 'twitter', 'twitterx', 'xtwitter'].includes(normalized)
		? 'twitter'
		: normalized;
};

export function SocialProfileAnalytics({ userId, className }: SocialProfileAnalyticsProps) {
	const [fetchingAnalytics, setFetchingAnalytics] = React.useState<Set<Id<'profiles'>>>(new Set());
	
	// Use the same query as SocialProfiles.tsx to get consistent results
	const userProfiles = useQuery(api.profiles.list, { 
		userId: userId,
		isSuperAdmin: false 
	});
	
	// Get profile analytics
	const profileAnalytics = useQuery(api.profileAnalytics.getAllProfileAnalytics);
	
	// Mutation to fetch analytics
	const fetchProfileAnalyticsAction = useAction(api.uploadPost.fetchProfileAnalytics);
	
	const handleFetchAnalytics = async (profile: Doc<'profiles'>) => {
		if (!profile.profileKey) return;
		
		setFetchingAnalytics(prev => new Set(prev).add(profile._id));
		
		try {
			await fetchProfileAnalyticsAction({
				profileId: profile._id,
			});
		} catch (error) {
			console.error('Error fetching analytics:', error);
		} finally {
			setFetchingAnalytics(prev => {
				const next = new Set(prev);
				next.delete(profile._id);
				return next;
			});
		}
	};
	
	const handleRefreshAll = async () => {
		if (!userProfiles) return;
		
		for (const profile of userProfiles) {
			if (profile.profileKey) {
				await handleFetchAnalytics(profile);
			}
		}
	};
	
	// Group profiles by their linked platforms
	const profilesWithPlatforms = React.useMemo(() => {
		if (!userProfiles) return [];
		
		
		const result: Array<{
			profile: typeof userProfiles[0];
			platform: string;
			analytics: Doc<'profileAnalytics'> | undefined;
		}> = [];
		
		const platformsAdded = new Set<string>(); // Prevent duplicates
		
		for (const profile of userProfiles) {
			// Check linkStatuses for connected platforms (same logic as SocialProfiles.tsx)
			if (profile.linkStatuses) {
				for (const linkStatus of profile.linkStatuses) {
					// Handle both new isLinked field and legacy status field
					const isConnected = linkStatus.isLinked ?? (linkStatus.status === 'linked');
					if (isConnected) {
						const canonicalPlatform = canonicalSocialPlatform(linkStatus.platform);
						const platformKey = `${profile._id}-${canonicalPlatform}`;
						if (!platformsAdded.has(platformKey)) {
							// Find analytics for this platform
							const analytics = profileAnalytics?.find(
								(a: any) =>
									a.profileId === profile._id &&
									canonicalSocialPlatform(a.platform) === canonicalPlatform
							);
							
							result.push({
								profile,
								platform: linkStatus.platform,
								analytics
							});
							platformsAdded.add(platformKey);
						}
					}
				}
			}
		}
		
		return result;
	}, [userProfiles, profileAnalytics]);
	
	if (!userProfiles || userProfiles.length === 0) {
		return null;
	}
	
	return (
		<div className={cn("space-y-6", className)}>
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center justify-between">
						<div className="flex items-center gap-2">
							<Share2 className="h-5 w-5" />
							Social Profile Analytics
						</div>
						<Button
							variant="outline"
							size="sm"
							onClick={handleRefreshAll}
							className="text-xs"
						>
							<RefreshCw className="h-3 w-3 mr-1" />
							Refresh All
						</Button>
					</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
						{profilesWithPlatforms.map(({ profile, platform, analytics }, index) => (
							<motion.div
								key={`${profile._id}-${platform}`}
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.3, delay: index * 0.1 }}
							>
								<GamifiedProfileCard
									profile={{
										...profile,
										// Use analytics-specific data if available
										profilePicture: analytics?.profilePicture || profile.profilePicture,
										username: analytics?.username || profile.username,
									}}
									platform={platform}
									metrics={analytics ? {
										followers: toMetricNumber(analytics.followers),
										following: toMetricNumber(analytics.following),
										posts: toMetricNumber(analytics.posts),
										engagement: toMetricNumber(analytics.engagement),
										views: toMetricNumber(analytics.views),
										engagementRate: toMetricNumber(analytics.avgEngagementRate),
										growthRate: toMetricNumber(analytics.followersGrowthRate),
									} : undefined}
									isLoading={fetchingAnalytics.has(profile._id)}
									onRefresh={() => handleFetchAnalytics(profile)}
									canEdit={false}
								/>
							</motion.div>
						))}
					</div>
					
					{profilesWithPlatforms.length === 0 && (
						<div className="text-center text-muted-foreground py-12">
							<Share2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
							<div className="text-lg font-medium mb-2">
								No Connected Social Profiles
							</div>
							<div className="text-sm">
								Connect your social media accounts to see analytics
							</div>
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
